import { DateTime, Duration, Interval } from "luxon";

import { colormaps, MultiPlotLayer, RawScalarField, RawVectorField, RawProfileField, ContourFillOptions, ContourOptions, BarbsOptions, HodographOptions } from "autumnplot-gl";
import { PlotComponent, ContourFill, Contour, Barbs, Hodographs } from "autumnplot-gl";
import { downloadGrid, downloadHodographs } from "./utils";

const CACHE_FMT = 'yyyy/MM/dd/HH';
const REMOTE_FMT = CACHE_FMT;
const LAYER_KEY_FMT = 'yyyyMMddHH';

// Defined at zoom 1
const THIN_FAC_BARBS = {
    'era5': 32,
    '20cr': 8,
}

const THIN_FAC_HODOS = 32;

type EnvironmentSource = 'era5' | '20cr';
type ArrayType = Float32Array;

interface ContourFillLayerSpec {
    type: 'contourfill';
    id: string;
    fields: string[];
    args: ContourFillOptions;
    func?: (...args: RawScalarField<ArrayType>[]) => RawScalarField<ArrayType>;
}

interface ContourLayerSpec {
    type: 'contour';
    id: string;
    fields: string[];
    args: ContourOptions;
    func?: (...args: RawScalarField<ArrayType>[]) => RawScalarField<ArrayType>;
}

interface BarbLayerSpec {
    type: 'barbs';
    id: string;
    fields: string[];
    args: BarbsOptions;
}

interface HodographLayerSpec {
    type: 'hodographs';
    id: string;
    fields: string[];
    args: HodographOptions;
}

type EnvironmentLayerSpec = ContourFillLayerSpec | ContourLayerSpec | BarbLayerSpec | HodographLayerSpec;

interface FieldDownloadResult {
    type: 'field';
    result: RawScalarField<ArrayType>;
}

interface HodographDownloadResult {
    type: 'hodograph';
    result: RawProfileField;
}

type DownloadResult = FieldDownloadResult | HodographDownloadResult;

interface ColorbarInfo {
    label: string;
    ticks: number[]; 
}

interface LayerSetSpec {
    layers: EnvironmentLayerSpec[];
    title: string;
    'short-title': string;
    dropdown: string;
    'colorbar-info': ColorbarInfo;
}

type LayerType = 't2m' | 'td2m' | 'spd500mb' | 'spd850mb' | 'hodocape';

const clamp = (x: number, min: number, max: number) => Math.max(min, Math.min(max, x));

const ENV_LAYERS: Record<LayerType, LayerSetSpec> = {
    't2m': {
        'layers': [
            {'type': 'contourfill', 'id': 't2m', 'fields': ['t2m'], 'args': {'cmap': colormaps.pw_t2m.withOpacity((levl, levu) => clamp((levl - 32) / 13, 0, 1)), 'opacity': 0.6}},
            {'type': 'contour', 'id': 'pmsl', 'fields': ['pmsl'], 'args': {'interval': 2, 'color': '#000000'}},
            {'type': 'barbs', 'id': 'barb10m', 'fields': ['u10m', 'v10m'], 'args': {'color': '#000000'}},
        ],
        'title': '2 m Temperature, MSLP, and 10 m Wind Barbs (kts)',
        'short-title': '2m T, MSLP, 10 m Winds',
        'dropdown': '2 m Temperature/MSLP/10 m Winds',
        'colorbar-info': {
            'label': '2 m Temperature (\u00b0F)',
            'ticks': [35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120]
        }
    },
    'td2m': {
        'layers': [
            {'type': 'contourfill', 'id': 'td2m', 'fields': ['td2m'], 'args': {'cmap': colormaps.pw_td2m.withOpacity((levl, levu) => clamp((levl - 40) / 15, 0, 1)), 'opacity': 0.6}},
            {'type': 'contour', 'id': 'pmsl', 'fields': ['pmsl'], 'args': {'interval': 2, 'color': '#000000'}},
            {'type': 'barbs', 'id': 'barb10m', 'fields': ['u10m', 'v10m'], 'args': {'color': '#000000'}}
        ],
        'title': '2 m Dewpoint, MSLP, and 10 m Wind Barbs (kts)',
        'short-title': '2 m Td, MSLP, 10 m Winds',
        'dropdown': '2 m Dewpoint/MSLP/10 m Winds',
        'colorbar-info': {
            'label': '2 m Dewpoint Temperature (\u00b0F)',
            'ticks': [45, 50, 55, 60, 65, 70, 75, 80, 85, 90]
        }
    },
    'spd500mb': {
        'layers': [
            {'type': 'contourfill', 'id': 'spd500mb', 'fields': ['u500mb', 'v500mb'], 'args': {'cmap': colormaps.pw_speed500mb, 'opacity': 0.6},
             'func': (u, v) => RawScalarField.aggregateFields(Math.hypot, u, v)},
            {'type': 'contour', 'id': 'height500mb', 'fields': ['height500mb'], 'args': {'interval': 30, 'color': '#000000'}},
            {'type': 'barbs', 'id': 'barb500mb', 'fields': ['u500mb', 'v500mb'], 'args': {'color': '#000000'}},
        ],
        'title': '500 mb Wind Speed, Height, and Wind Barbs (kts)',
        'short-title': '500 mb Winds, Heights',
        'dropdown': '500 mb Wind/Height',
        'colorbar-info': {
            "label": "500 mb Wind Speed (kts)",
            "ticks": [25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130, 135, 140]
        }
    },
    'spd850mb': {
        'layers': [
            {'type': 'contourfill', 'id': 'spd850mb', 'fields': ['u850mb', 'v850mb'], 'args': {'cmap': colormaps.pw_speed850mb, 'opacity': 0.6},
             'func': (u, v) => RawScalarField.aggregateFields(Math.hypot, u, v)},
            {'type': 'contour', 'id': 'height850mb', 'fields': ['height850mb'], 'args': {'interval': 15, 'color': '#000000'}},
            {'type': 'barbs', 'id': 'barb850mb', 'fields': ['u850mb', 'v850mb'], 'args': {'color': '#000000'}},
        ],
        'title': '850 mb Wind Speed, Height, and Wind Barbs (kts)',
        'short-title': '850 mb Winds, Heights',
        'dropdown': '850 mb Wind/Height',
        'colorbar-info': {
            "label": "850 mb Wind Speed (kts)",
            "ticks": [25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80]
        }
    },
    'hodocape': {
        'layers': [
            {'type': 'contourfill', 'id': 'cape', 'fields': ['cape'], 'args': {'cmap': colormaps.pw_cape.withOpacity((levl, levu) => clamp(levl / 500, 0, 1)), 'opacity': 0.6}},
            {'type': 'barbs', 'id': 'barb10m', 'fields': ['u10m', 'v10m'], 'args': {'color': '#505050'}},
            {'type': 'hodographs', 'id': 'hodographs', 'fields': ['hodographs'], 'args': {'bgcolor': '#3a3a3a'}}
        ],
        'title': 'SBCAPE and Hodographs',
        'short-title': 'SBCAPE, Hodographs',
        'dropdown': 'SBCAPE/Hodographs',
        'colorbar-info': {
            "label": "CAPE (J/kg)",
            "ticks": [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 8000, 10000]
        }
    }
};

const delay = (delay_dt: number) => {
    return new Promise((resolve, reject) => {
        window.setTimeout(resolve, delay_dt);
    });
}

class EnvironmentDataInterface {
    readonly base_path: string;
    readonly domain: string;
    readonly source: EnvironmentSource;
    readonly available_dates: DateTime[];
    readonly available_date_strs: string[];
    readonly dt_analysis: Duration;

    constructor(base_path: string, domain: string, source: EnvironmentSource, available_date_strs: string[], dt_analysis: Duration) {
        this.base_path = base_path;
        this.domain = domain;
        this.source = source;
        this.available_date_strs = available_date_strs;
        this.available_dates = this.available_date_strs.map(dt => DateTime.fromFormat(dt, 'yyyy-MM-dd', {'zone': 'utc'}));
        this.dt_analysis = dt_analysis;
    }

    static async fromServer(base_path: string, domain: string, source: EnvironmentSource) {
        const now = DateTime.utc();
        const resp = await fetch(base_path + '/get_env_dates.php?dom=' + domain + '&src=' + source + '&' + now.toISO());
        const data = await resp.json();
        return new EnvironmentDataInterface(base_path, domain, source, data['available_dates'], Duration.fromISO('PT' + data['data_time_step'] + 'H'));
    }

    hasData(dt: DateTime, cday_offset: Duration) {
        return this.available_date_strs.includes(dt.minus(cday_offset).toFormat('yyyy-MM-dd'));
    }

    availableDaysInIntervals(intvs: Interval[], cday_offset: Duration) {
        const intvs_shift = intvs.map(intv => Interval.fromDateTimes(intv.start.minus(cday_offset), intv.end.minus(cday_offset)));
        const full_intv = Interval.merge(intvs_shift)[0];
        const dates_in_full_intv = this.available_dates.filter(dt => full_intv.contains(dt));

        return intvs_shift.map(intv_shift => dates_in_full_intv.map(dt => intv_shift.contains(dt) ? 1 : 0).reduce((a: number, b: number) => a + b, 0));
    }

    hasDates() {
        return this.available_date_strs.length > 0;
    }

    getLayers(type: LayerType) {
        const lyr_specs = ENV_LAYERS[type]['layers'];
        return lyr_specs.map(lyr_spec => {return {'spec': lyr_spec, 'layer': new MultiPlotLayer(lyr_spec['id'])}; });
    }

    populateLayers(layer_objs: {'spec': EnvironmentLayerSpec, 'layer': MultiPlotLayer}[], datetimes: DateTime[], default_dt: DateTime) {
        if (layer_objs.length == 0) {
            return;
        }

        const layers = layer_objs.map(lyr_obj => lyr_obj['layer']);
        const lyr_specs = layer_objs.map(lyr_obj => lyr_obj['spec']);

        const dt_from_default = (dt: DateTime) => Math.abs(dt.toMillis() - default_dt.toMillis());
        datetimes.sort((a, b) => dt_from_default(a) - dt_from_default(b));

        const layer_dts_millis = layers[0].getKeys().map(key => DateTime.fromFormat(key, LAYER_KEY_FMT, {'zone': 'utc'}).toMillis());
        layer_dts_millis.sort((a, b) => a - b);
        datetimes = datetimes.filter(dt => !layer_dts_millis.includes(dt.toMillis()));

        if (datetimes.length == 0) {
            return;
        }

        console.log("Downloading layers for " + datetimes.length + (datetimes.length == 1 ? ' time' : ' times'));

        // Uniquify fields and only download fields once, even if they're mentioned multiple times (like 500mb u and v)
        let fields = lyr_specs.map(lyr_spec => lyr_spec['fields']).flat();
        fields = [...new Set(fields)];

        // Pull the download functions out of the layer classes
        let downloaders: Record<string, (url: string) => Promise<DownloadResult>> = {};
        lyr_specs.forEach(lyr_spec => {
            lyr_spec['fields'].forEach(field_name => {
                if (lyr_spec['type'] === 'hodographs') {
                    downloaders[field_name] = async (url: string): Promise<HodographDownloadResult> => {
                        const data = await downloadHodographs(url); 
                        return {'type': 'hodograph', 'result': data};
                    };
                }
                else {
                    downloaders[field_name] = async (url: string): Promise<FieldDownloadResult> => {
                        const data = await downloadGrid(url); 
                        return {'type': 'field', 'result': data};
                    };
                }
            })
        })

        datetimes.forEach(async dt => {
            // Do downloads
            const promises = fields.map(field_name => {
                const downloader = downloaders[field_name];
                const rem_fname = this.base_path + '/' + this.domain + '/' + this.source + '/' + dt.toFormat(REMOTE_FMT) + '/' + field_name + '.dat';
                return downloader(rem_fname);
            });

            // Handle downloaded data
            Promise.allSettled(promises).then(results => {
                let result_vals = Object.fromEntries(results.map((res, ires) => [fields[ires], res]));

                Object.entries(result_vals).forEach(([k, v]) => {
                    if (v.status == 'rejected') {
                        console.error('Download for ' + k + ' failed: ' + v.reason);
                    }
                })

                lyr_specs.forEach((lyr_spec, ilyr) => {
                    const lyr_fields: DownloadResult[] = lyr_spec['fields'].map(field_name => result_vals[field_name])
                                                                           .filter((res): res is PromiseFulfilledResult<DownloadResult> => res.status == 'fulfilled')
                                                                           .filter(val => val !== undefined).map(res => res.value);

                    if (lyr_fields.length < lyr_spec['fields'].length) {
                        // If we don't have all the fields we expect, bail out
                        return;
                    }
                                                    
                    const allFields = (lyrf: any[]): lyrf is FieldDownloadResult[] => {
                        return lyrf.map(lyrf => lyrf['type'] == 'field').reduce((a, b) => a && b);
                    }

                    const allHodos = (lyrf: any[]): lyrf is HodographDownloadResult[] => {
                        return lyrf.map(lyrf => lyrf['type'] == 'hodograph').reduce((a, b) => a && b);
                    }

                    let field: PlotComponent;
                    if (allFields(lyr_fields)) {
                        const maybe_apply_func = (lyr_spec: ContourFillLayerSpec | ContourLayerSpec) => {
                            let data_arg: RawScalarField<ArrayType>;
                            if (lyr_spec.func !== undefined) {
                                const agg_field = lyr_spec['func'](...lyr_fields.map(fld => fld['result']));
                                data_arg = agg_field;
                            }
                            else {
                                data_arg = lyr_fields[0]['result'];
                            }
                            return data_arg;
                        }

                        switch (lyr_spec['type']) {
                            case 'contourfill':
                                field = new ContourFill(maybe_apply_func(lyr_spec), lyr_spec['args']);
                                break;
                            case 'contour':
                                field = new Contour(maybe_apply_func(lyr_spec), lyr_spec['args']);
                                break;
                            case 'barbs':
                                const lyr_args = {
                                    ...lyr_spec['args'],
                                    'thin_fac': THIN_FAC_BARBS[this.source]
                                };

                                const u_field = lyr_fields[0]['result'];
                                const v_field = lyr_fields[1]['result'];
                                const vector_field = new RawVectorField(u_field.grid, u_field.data, v_field.data);
                                field = new Barbs(vector_field, lyr_args);
                                break;
                            default:
                                throw "Fields dowloaded to a hodograph layer";
                        }
                    }
                    else if (allHodos(lyr_fields)) {
                        if (lyr_spec['type'] == 'hodographs') {
                            const lyr_args = {
                                ...lyr_spec['args'],
                                'thin_fac': THIN_FAC_HODOS
                            };

                            field = new Hodographs(lyr_fields[0]['result'], lyr_args);
                        }
                        else {
                            throw "Hodographs downloaded to a non-hodograph layer";
                        }
                    }
                    else {
                        throw "Got mixed field and hodograph downloads in populateLayers()";
                    }

                    layers[ilyr].addField(field, dt.toFormat(LAYER_KEY_FMT));
                });
            });

            await delay(50);
        });

        return layers;
    }

    static getColormaps(type: LayerType | null) {
        if (type === null) {
            return null;
        }

        const layers = ENV_LAYERS[type]['layers'].filter((lyr_spec: any): lyr_spec is ContourFillLayerSpec => lyr_spec['type'] == 'contourfill');
        const colormaps_found = layers.map(lyr_spec => lyr_spec['args']).map(args => args['cmap']).filter(cmap => cmap !== undefined);

        if (colormaps_found.length > 1) {
            console.warn("More than one colormap. Using the first one.");
        }

        const colormap = colormaps_found[0];
        return {colormap: colormap, colorbar_info: ENV_LAYERS[type]['colorbar-info']};
    }

    getTitle(type: LayerType, which?: string) {
        if (which === undefined) {
            which = 'normal';
        }

        let title: string;
        if (which == 'short') {
            title = ENV_LAYERS[type]['short-title'];
        }
        else {
            title = ENV_LAYERS[type]['title'];
        }
        return this.source.toUpperCase() + " " + title;
    }

    static getAvailableTypes() {
        return Object.keys(ENV_LAYERS) as LayerType[];
    }

    static getAvailableReanalyses() {
        return ['era5', '20cr'] as EnvironmentSource[];
    }

    getAnalysisTimeStep() {
        return this.dt_analysis;
    }

    static getDropdownEntry(type: LayerType) {
        return ENV_LAYERS[type]['dropdown'];
    }
}

export { EnvironmentDataInterface, LAYER_KEY_FMT };
export type {EnvironmentLayerSpec, LayerType, EnvironmentSource}